// The bridging engine's markup, ported from the bridgingeye.co.uk homepage.
// Two parts that a page can place separately: the quick-estimate card and
// the flow (refine sections, results, adviser band). The "Get exact terms"
// dialog (sharedMarkup.ts) goes with the flow. Links that used to be #anchors are buttons
// with data-scroll, since hash links can't reach inside a shadow root.
import { escapeHtml } from '../format';
import type { EngineConfig } from '../types';

const INFO_SVG = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 11v5M12 8h.01"/></svg>';

function tip(text: string, about: string, extra = ''): string {
  return `<button type="button" class="info-tip" ${extra} data-tip="${escapeHtml(text)}" aria-label="More about ${about}">?</button>`;
}

export function estimateHtml(config: EngineConfig, flatCard: boolean): string {
  const { form } = config;
  return `
<div class="estimate-wrap">
  <div class="estimate-card${flatCard ? ' flat' : ''}" role="region" aria-labelledby="estimate-title">
    <div class="card-head">
      <h2 id="estimate-title" class="estimate-title">Quick estimate</h2>
      <span class="live-dot"><span><span id="estimate-product-count">—</span> available bridging products</span></span>
    </div>

    <div class="field">
      <div class="label-row">
        <span class="label-group"><label for="input-value">Property value / purchase price</label>${tip("If you're purchasing or refinancing multiple properties, enter the total value of all", 'estimated property value')}</span>
      </div>
      <div class="money-field"><span class="currency" aria-hidden="true">£</span><input type="text" inputmode="numeric" autocomplete="off" id="input-value" /></div>
    </div>

    <div class="field">
      <div class="label-row loan-label-row">
        <span class="label-group"><label for="input-loan">Loan amount required</label>${tip('Please include any existing mortgages that need to be cleared', 'loan amount required')}</span>
        <div id="ltv-section" class="ltv-presets" role="group" aria-label="Set the loan to a loan-to-value">
          <span class="hint">Quick LTV</span>
          ${form.quickLtvPcts.map((v) => `<button type="button" class="chip chip-sm" data-quick-ltv="${v}" aria-label="Set loan to ${v}% of the property value">${v}%</button>`).join('')}
        </div>
      </div>
      <div class="money-field is-accent"><span class="currency" id="input-loan-prefix" aria-hidden="true">£</span><input type="text" inputmode="numeric" autocomplete="off" id="input-loan" aria-describedby="ltv-badge ltv-note" /><span class="ltv-badge" id="ltv-badge"><span id="ltv-label">0.0%</span> LTV</span></div>
      <div class="loan-tools">
        <p id="ltv-note" class="hint"></p>
        <button type="button" class="chip chip-sm max-loan-btn" id="max-loan-toggle-btn" aria-pressed="false">Maximum net loan available</button>
      </div>
    </div>

    <div class="field">
      <div class="label-row">
        <label for="input-term-slider">Term needed</label>
        <span id="term-label" class="term-value"></span>
      </div>
      <p id="term-regulated-note" class="hint hint-accent hidden">A maximum of ${form.term.regulatedMax} months applies for regulated bridging loans where you or a member of your family will occupy at least 40% of the property.</p>
      <div class="term-row">
        <input type="range" class="term-slider" id="input-term-slider" min="${form.term.min}" max="${form.term.max}" step="1" aria-describedby="term-label" />
        <input class="control term-number" type="number" id="input-term-number" min="${form.term.min}" max="${form.term.max}" aria-label="Term in months" />
      </div>
    </div>

    <div class="rate-panel" aria-live="polite">
      <div class="rate-panel-top">
        <span class="rate-panel-label">Indicative rate from</span>
        <p id="rate-panel-headline" class="rate-panel-headline">Checking available products…</p>
        <div id="rate-panel-figure-wrap" class="rate-figure hidden"><span id="rate-panel-figure"></span><span class="pm">pm</span></div>
      </div>
      <div id="rate-panel-breakdown" class="rate-breakdown hidden">
        <div><div class="k">Gross loan</div><div class="v" id="rate-panel-gross"></div></div>
        <div><div class="k">Interest</div><div class="v" id="rate-panel-interest"></div></div>
        <div><div class="k">Total cost</div><div class="v" id="rate-panel-total"></div></div>
      </div>
    </div>

    <button type="button" class="btn btn-primary btn-block btn-lg" data-scroll="refine">Refine your case and see available products →</button>
    <p class="hint" style="font-size:12.5px">Indicative only, not an offer. Interest is shown rolled up over the full term with no early-repayment credit; arrangement fees are usually deducted from the advance.</p>
  </div>
</div>`;
}

function section(id: string, n: number, title: string, shortTitle: string, lede: string, body: string, continueButton: string): string {
  return `
        <section class="q-section" id="sec-${id}" data-section="${id}" aria-labelledby="sec-${id}-title">
          <div class="q-summary">
            <div><span class="q-summary-title">${n} · ${title}</span><span class="q-summary-text" data-summary="${id}"></span></div>
            <button type="button" class="link-btn" data-change="${id}" aria-label="Change ${shortTitle.toLowerCase()}">Change</button>
          </div>
          <div class="q-body">
            <div class="q-head">
              <span class="q-badge" data-badge="${id}" aria-hidden="true">${n}</span>
              <div><h3 id="sec-${id}-title">${title}</h3><p>${lede}</p></div>
            </div>
            ${body}
            ${continueButton}
          </div>
        </section>`;
}

function pillsQuestion(id: string, label: string, pillsId: string): string {
  return `
            <div class="q">
              <p class="q-label" id="q-${id}">${label}</p>
              <div class="pills" role="group" aria-labelledby="q-${id}" id="${pillsId}" data-advance-stop></div>
            </div>`;
}

/** The development finance enquiry form on stirlingpartners.co.uk (src/developmentEnquiry.ts), for what's clearly not bridging. */
function developmentEnquiryLink(brandId: string): string {
  return `<a href="https://stirlingpartners.co.uk/development-finance.html?from=${encodeURIComponent(brandId)}#enquiry">Tell us about your project</a>`;
}

const refurbFields = (brandId: string) => `
              <div id="refurb-fields" class="refurb-panel hidden" role="group" aria-labelledby="refurb-title">
                <p class="refurb-title" id="refurb-title">Refurbishment details</p>
                <fieldset class="refurb-row">
                  <legend>Purchase costs</legend>
                  <div class="pair" id="purchase-costs-pair">
                    <div class="affix-field"><label class="visually-hidden" for="input-purchase-costs-pct">Purchase costs, percent of purchase price</label><input class="control" type="number" inputmode="decimal" min="0" max="100" step="0.1" id="input-purchase-costs-pct" value="5" data-advance-stop /><span class="affix" aria-hidden="true">%</span></div>
                    <div class="affix-field"><span class="affix" aria-hidden="true">£</span><label class="visually-hidden" for="input-purchase-costs-gbp">Purchase costs, in pounds</label><input class="control" type="text" inputmode="numeric" autocomplete="off" id="input-purchase-costs-gbp" /></div>
                  </div>
                  <p class="hint">Stamp duty, legal and other costs of buying, as a percentage of the purchase price.</p>
                  <label class="consent refurb-owned"><input type="checkbox" id="input-already-owned" /><span>Property already owned by the client</span></label>
                </fieldset>
                <fieldset class="refurb-row">
                  <legend>Refurbishment costs</legend>
                  <div class="pair">
                    <div class="field"><label for="input-build-costs">Build costs</label><div class="affix-field"><span class="affix" aria-hidden="true">£</span><input class="control" type="text" inputmode="numeric" autocomplete="off" id="input-build-costs" placeholder="0" /></div></div>
                    <div class="field"><label for="input-professional-costs">Professional costs</label><div class="affix-field"><span class="affix" aria-hidden="true">£</span><input class="control" type="text" inputmode="numeric" autocomplete="off" id="input-professional-costs" placeholder="0" /></div></div>
                  </div>
                  <p class="hint">Total refurbishment costs: <strong id="refurb-total">£0</strong></p>
                </fieldset>
                <fieldset class="refurb-row">
                  <legend>Contingency</legend>
                  <div class="pair">
                    <div class="affix-field"><label class="visually-hidden" for="input-contingency-pct">Contingency, percent of refurbishment costs</label><input class="control" type="number" inputmode="decimal" min="0" max="100" step="0.1" id="input-contingency-pct" value="5" /><span class="affix" aria-hidden="true">%</span></div>
                    <div class="affix-field is-readonly"><span class="affix" aria-hidden="true">£</span><label class="visually-hidden" for="output-contingency-gbp">Contingency, in pounds</label><output class="control" id="output-contingency-gbp">0</output></div>
                  </div>
                  <p class="hint">Percentage of the total refurbishment costs.</p>
                </fieldset>
                <div class="field refurb-row">
                  <label for="input-gdv">Gross development value (GDV)</label>
                  <div class="affix-field gdv-field"><span class="affix" aria-hidden="true">£</span><input class="control" type="text" inputmode="numeric" autocomplete="off" id="input-gdv" aria-describedby="gdv-hint gdv-error" /></div>
                  <p class="hint" id="gdv-hint">The property's value once the works are finished. Must be more than the purchase price.</p>
                  <p class="form-error hidden" id="gdv-error" role="alert"></p>
                </div>
                <div id="refurb-development-note" class="notice">
                  ${INFO_SVG}
                  <span><strong>Building new homes, or demolishing and rebuilding?</strong> That's usually development finance rather than bridging. ${developmentEnquiryLink(brandId)}</span>
                </div>
              </div>`;

export function flowHtml(config: EngineConfig): string {
  const { brand, form } = config;
  const continueBtn = (id: string) => `<button type="button" class="btn btn-primary btn-block mobile-only" data-continue="${id}">Continue</button>`;
  const purpose = section('purpose', 1, 'Purpose &amp; occupancy', 'Purpose and occupancy', 'This decides whether the loan is regulated.', `
            <div class="q">
              <p class="q-label" id="q-purpose">What's the loan for?</p>
              <div class="pills" role="group" aria-labelledby="q-purpose" id="input-purpose" data-advance-stop></div>
              ${refurbFields(brand.id)}
              <div id="below-value-fields" class="refurb-panel hidden">
                <label class="consent"><input type="checkbox" id="input-below-value" /><span>Buying below the property's value?</span></label>
                <div class="field hidden" id="purchase-price-field" style="margin-top:10px;max-width:360px">
                  <label for="input-purchase-price">Purchase price</label>
                  <div class="affix-field"><span class="affix" aria-hidden="true">£</span><input class="control" type="text" inputmode="numeric" autocomplete="off" id="input-purchase-price" aria-describedby="purchase-price-hint" /></div>
                  <p class="hint" id="purchase-price-hint">Some lenders lend less against the price than the value.</p>
                </div>
              </div>
            </div>
            <div class="q hidden" id="similar-project-q">
              <p class="q-label" id="q-similar-project">Have you done a project like this before?</p>
              <div class="pills" role="group" aria-labelledby="q-similar-project" id="input-similar-project" data-advance-stop></div>
            </div>
            <div class="q">
              <p class="q-label" id="q-exit">How will the loan be repaid?</p>
              <div class="pills" role="group" aria-labelledby="q-exit" id="input-exit" data-advance-stop></div>
            </div>
            <div class="q">
              <p class="q-label" id="q-live-in">Will you or a family member live there?</p>
              <div class="pills" role="group" aria-labelledby="q-live-in" id="liveIn-seg" data-advance-stop></div>
              <div id="live-in-regulated-note" class="notice hidden" role="status">
                ${INFO_SVG}
                <span><strong>This will be a regulated bridging loan.</strong> Maximum term ${form.term.regulatedMax} months. <button type="button" class="link-btn" id="change-term-link">Change term</button></span>
              </div>
            </div>`, continueBtn('purpose'));
  const property = section('property', 2, 'The property', 'The property', 'What the loan is secured against, and where.', `
            ${pillsQuestion('property-type', 'Security property', 'input-property-type')}
            <div id="land-development-note" class="notice hidden" role="status">
              ${INFO_SVG}
              <span><strong>Planning to build on the land?</strong> Bridging can fund buying it, but the build itself is usually funded with development finance. ${developmentEnquiryLink(brand.id)}</span>
            </div>
            <div class="q-row">
              <div class="field hidden" id="property-subtype-field">
                <label for="input-property-subtype">Property subtype</label>
                <select id="input-property-subtype" class="control" data-advance-stop></select>
              </div>
              <div class="field">
                <label for="input-region-group">Where is the security?</label>
                <select id="input-region-group" class="control" data-advance-stop>${form.regionGroups.map(([v, l]) => `<option value="${v}">${escapeHtml(l)}</option>`).join('')}</select>
              </div>
              <div class="field postcode-field" id="postcode-field">
                <label for="input-postcode">Postcode</label>
                <input id="input-postcode" class="control postcode-input" data-advance-stop placeholder="e.g. SW1A 1AA" autocomplete="postal-code" />
              </div>
            </div>
            <div class="q">
              <p class="q-label" id="q-charge">Charge required</p>
              <div class="pills" role="group" aria-labelledby="q-charge" id="charge-seg" data-advance-stop></div>
              <div class="field hidden" id="first-charge-field" style="margin-top:14px;max-width:360px">
                <label for="input-first-charge">Outstanding balance on the existing mortgage(s)</label>
                <div class="affix-field"><span class="affix" aria-hidden="true">£</span><input class="control" type="text" inputmode="numeric" autocomplete="off" id="input-first-charge" data-advance-stop aria-describedby="first-charge-hint" /></div>
                <p class="hint" id="first-charge-hint">What's still owed on any mortgage or loan already secured on the property. Lenders check the combined total against the property value.</p>
              </div>
            </div>`, continueBtn('property'));
  const applicant = section('applicant', 3, 'The applicant', 'The applicant', 'Who is borrowing and their recent credit history.', `
            ${pillsQuestion('applicant', 'Who is the applicant?', 'input-applicant-type')}
            ${pillsQuestion('adverse', 'Any adverse credit in the last 3 years?', 'input-adverse')}`,
  '<button type="button" class="btn btn-primary btn-lg view-products-btn" data-continue="applicant">View eligible products →</button>');

  const progressItem = (id: string, n: number, long: string, short: string, target: string) =>
    `<li data-progress="${id}"><button type="button" data-scroll="${target}"><span class="num">${n}</span><span><span class="long">${long}</span><span class="short-label">${short}</span></span></button></li>`;

  return `
<div class="flow-wrap">
<div class="case-flow">
  <div class="case-bar" role="region" aria-label="Your case">
    <div>
      <span class="case-bar-label">Your case</span>
      <span class="case-bar-values"><span data-case="loan">—</span> · <span class="hl"><span data-case="ltv">—</span> LTV</span> · <span data-case="term-short">—</span></span>
    </div>
    <button type="button" class="btn btn-ghost-light btn-sm" data-scroll="estimate">Edit</button>
  </div>

  <section id="refine" class="refine" aria-labelledby="refine-title">
    <div class="wrap refine-head">
      <div>
        <p class="eyebrow">Step 2 of 2 · Refine</p>
        <h2 id="refine-title" class="display">Products priced <em class="accent">for your case.</em></h2>
        <p class="lede">Three short sections: the purpose, the property, and you. Available products update as you answer.</p>
      </div>
      <button type="button" class="btn btn-outline" data-scroll="advisers">Not sure? Let an adviser choose →</button>
    </div>

    <div class="wrap refine-grid">
      <aside class="refine-aside">
        <div class="case-card on-dark">
          <div class="case-card-head"><h3>Your case</h3><button type="button" class="link-btn" data-scroll="estimate">Edit</button></div>
          <dl>
            <div><dt>Property value</dt><dd data-case="value">—</dd></div>
            <div><dt>Loan amount</dt><dd data-case="loan">—</dd></div>
            <div><dt>Loan to value</dt><dd class="hl" data-case="ltv">—</dd></div>
            <div><dt>Term</dt><dd data-case="term">—</dd></div>
          </dl>
          <p class="case-note">Figures carried over from the quick estimate.</p>
        </div>
        <nav class="progress" aria-label="Refine progress">
          <ol>
            ${progressItem('purpose', 1, 'Purpose &amp; occupancy', 'Purpose', 'sec-purpose')}
            ${progressItem('property', 2, 'The property', 'Property', 'sec-property')}
            ${progressItem('applicant', 3, 'The applicant', 'Applicant', 'sec-applicant')}
            ${progressItem('compare', 4, 'Compare products', 'Compare', 'results')}
          </ol>
        </nav>
      </aside>

      <div class="refine-sections">${purpose}${property}${applicant}
      </div>
    </div>
  </section>

  <section id="results" class="results" aria-labelledby="results-headline">
    <div class="wrap">
      <div class="results-head">
        <div>
          <p class="eyebrow">4 · Compare products</p>
          <h2 id="results-headline" class="display" aria-live="polite" tabindex="-1">Complete the details above</h2>
          <p class="results-sub">Gross loan <strong id="results-loan-label"></strong><span id="results-criteria-line" class="criteria-line"></span></p>
          <div id="apply-pick" class="apply-pick hidden" role="status"></div>
        </div>
        <div id="results-sort-wrap" class="field sort hidden">
          <label for="input-results-sort">Sort by</label>
          <select id="input-results-sort" class="control">
            <option value="totalCost">Total cost (lowest first)</option>
            <option value="rate">Interest rate (lowest first)</option>
            <option value="maxLoan">Maximum loan available (highest first)</option>
          </select>
        </div>
      </div>

      <div id="results-wrap" class="hidden">
        <div class="results-table" id="results-table">
          <div class="results-header" id="results-header-grid"></div>
          <ol class="results-rows" id="results-rows"></ol>
          <div class="results-more hidden" id="results-more-wrap">
            <button type="button" class="btn btn-soft" id="results-show-all-btn"></button>
          </div>
        </div>
        <p class="fine">Interest is shown rolled up over the full term with no early-repayment credit; arrangement fees are usually deducted from the advance. Indicative only, not an offer.</p>

      </div>

      <div id="not-ready-wrap" class="empty">
        <p class="text-muted">Answer every question in the refine section above and matching lenders will appear here automatically.</p>
      </div>

      <div id="no-results-wrap" class="empty hidden">
        <h3>No product matches these figures</h3>
        <p>No available products currently match this combination of loan size, LTV, term, property type, region and credit profile.</p>
        <p class="text-muted">Adjust the loan amount, term or credit answer above and the list will refill.</p>
      </div>

        <form class="email-results callback-request hidden" id="callback-form" novalidate aria-labelledby="callback-title">
          <div class="email-results-copy">
            <h3 id="callback-title">Rather talk it through?</h3>
            <p id="callback-intro">Leave your number and a broker will call you about these figures. No credit search, no obligation. See our <a href="${escapeHtml(brand.privacyUrl)}" target="_blank" rel="noopener">Privacy Policy</a>.</p>
          </div>
          <div class="email-results-fields callback-fields">
            <div class="field"><label for="callback-name">First name</label><input class="control" type="text" id="callback-name" autocomplete="given-name" maxlength="40" /></div>
            <div class="field"><label for="callback-phone">Phone</label><input class="control" type="tel" id="callback-phone" autocomplete="tel" maxlength="30" /></div>
            <div class="field"><label for="callback-email">Email</label><input class="control" type="email" id="callback-email" autocomplete="email" maxlength="200" /></div>
            <div class="field"><label for="callback-time">Best time</label><select class="control" id="callback-time"><option>Any time</option><option>Morning</option><option>Lunchtime</option><option>Afternoon</option></select></div>
            <div class="visually-hidden" aria-hidden="true"><label for="callback-company">Company</label><input type="text" id="callback-company" tabindex="-1" autocomplete="off" /></div>
            <button type="submit" class="btn btn-primary" id="callback-btn">Call me back</button>
          </div>
          <p class="hint hidden" id="callback-status" role="status"></p>
          <p class="form-error hidden" id="callback-error" role="alert"></p>
        </form>

        <form class="email-results hidden" id="email-results-form" novalidate aria-labelledby="email-results-title">
          <div class="email-results-copy">
            <h3 id="email-results-title">Not ready to talk yet?</h3>
            <p>We'll email you these products so you can come back to them. An adviser may follow up once by email to see if we can help. See our <a href="${escapeHtml(brand.privacyUrl)}" target="_blank" rel="noopener">Privacy Policy</a>.</p>
          </div>
          <div class="email-results-fields">
            <div class="field"><label for="email-results-name">First name</label><input class="control" type="text" id="email-results-name" autocomplete="given-name" maxlength="40" /></div>
            <div class="field"><label for="email-results-email">Email</label><input class="control" type="email" id="email-results-email" autocomplete="email" maxlength="200" /></div>
            <div class="visually-hidden" aria-hidden="true"><label for="email-results-company">Company</label><input type="text" id="email-results-company" tabindex="-1" autocomplete="off" /></div>
            <button type="submit" class="btn btn-outline" id="email-results-btn">Email me these results</button>
          </div>
          <p class="hint hidden" id="email-results-status" role="status"></p>
          <p class="form-error hidden" id="email-results-error" role="alert"></p>
        </form>
    </div>
  </section>

  <section id="advisers" class="adviser-band on-dark" aria-labelledby="advisers-title">
    <div class="wrap adviser-inner">
      <div>
        <h2 id="advisers-title" class="display">Not sure which product fits?</h2>
        <p>Our advisers review your case across the whole market and negotiate terms for you. No credit search is run at this stage.</p>
      </div>
      <div class="adviser-actions">
        <button type="button" class="btn btn-on-dark btn-lg" id="advisers-select-btn">Let an adviser find a product →</button>
        <a href="tel:${escapeHtml(brand.phone.replace(/\s/g, ''))}" class="btn btn-ghost-light btn-lg">${escapeHtml(brand.phone)}</a>
      </div>
    </div>
  </section>
</div>
</div>`;
}
